import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  LogOut,
  RefreshCw,
  Mail,
  Phone,
  MapPin,
  Calendar,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  Home,
  Shield,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  new: { label: "New", color: "bg-blue-100 text-blue-700", icon: AlertCircle },
  contacted: { label: "Contacted", color: "bg-yellow-100 text-yellow-700", icon: Phone },
  in_progress: { label: "In Progress", color: "bg-purple-100 text-purple-700", icon: Clock },
  completed: { label: "Completed", color: "bg-green-100 text-green-700", icon: CheckCircle },
  closed: { label: "Closed", color: "bg-gray-100 text-gray-700", icon: FileText },
};

const situationLabels = {
  "think-damage": "Thinks roof has storm damage",
  "know-damage": "Knows has storm damage",
  "insurance-claim": "Working through insurance claim",
  "repair-options": "Needs repair options",
  "replacement-options": "Needs replacement options",
};

const insuranceReasonLabels = {
  "better-rates": "Looking for better rates",
  "better-coverage": "Need better storm coverage",
  "bad-experience": "Had a bad claims experience",
  "local-agent": "Want a local agent who understands Minnesota",
  "other": "Other reason",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("leads");

  // Use sessionStorage instead of localStorage for better security
  const token = sessionStorage.getItem("adminToken");

  // Set admin-specific SEO (noindex)
  useEffect(() => {
    document.title = "Lead Dashboard | Minnesota Storm Damage Admin";
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (robotsMeta) {
      robotsMeta.setAttribute('content', 'noindex, nofollow');
    }
    return () => {
      if (robotsMeta) {
        robotsMeta.setAttribute('content', 'index, follow');
      }
    };
  }, []);

  const fetchLeads = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("adminToken");
        navigate("/admin");
      }
      toast.error("Failed to load leads");
    }
  }, [token, navigate]);

  const fetchReferrals = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/insurance-referrals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReferrals(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("adminToken");
        navigate("/admin");
      }
      toast.error("Failed to load insurance referrals");
    }
  }, [token, navigate]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchLeads(), fetchReferrals()]);
    setLoading(false);
    setRefreshing(false);
  }, [fetchLeads, fetchReferrals]);

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchAll();
  }, [token, navigate, fetchAll]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAll();
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminToken");
    navigate("/admin");
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await axios.patch(
        `${API}/leads/${leadId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeads((prev) =>
        prev.map((lead) =>
          lead.id === leadId ? { ...lead, status: newStatus } : lead
        )
      );
      toast.success("Status updated");
    } catch {
      toast.error("Failed to update status");
    }
  };

  const updateReferralStatus = async (referralId, newStatus) => {
    try {
      await axios.patch(
        `${API}/insurance-referrals/${referralId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReferrals((prev) =>
        prev.map((ref) =>
          ref.id === referralId ? { ...ref, status: newStatus } : ref
        )
      );
      toast.success("Status updated");
    } catch {
      toast.error("Failed to update status");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const stats = {
    total: leads.length,
    new: leads.filter((l) => l.status === "new").length,
    contacted: leads.filter((l) => l.status === "contacted").length,
    in_progress: leads.filter((l) => l.status === "in_progress").length,
    completed: leads.filter((l) => l.status === "completed").length,
  };

  const referralStats = {
    total: referrals.length,
    new: referrals.filter((r) => r.status === "new").length,
    contacted: referrals.filter((r) => r.status === "contacted").length,
    completed: referrals.filter((r) => r.status === "completed").length,
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-navy border-b border-navy-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-orange/20 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-orange" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-semibold text-white">
                Admin Dashboard
              </h1>
              <p className="text-gray-400 text-sm">Minnesota Storm Damage</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/")}
              className="border-navy-border text-gray-300 hover:text-white hover:bg-navy-light"
            >
              <Home className="w-4 h-4 mr-2" />
              View Site
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="border-navy-border text-gray-300 hover:text-white hover:bg-navy-light"
            >
              <RefreshCw
                className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-gray-400 hover:text-white hover:bg-navy-light"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Content with Tabs */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 bg-white border border-gray-200">
            <TabsTrigger value="leads" className="flex items-center gap-2 data-[state=active]:bg-orange data-[state=active]:text-white">
              <Users className="w-4 h-4" />
              Storm Damage Leads ({stats.total})
            </TabsTrigger>
            <TabsTrigger value="referrals" className="flex items-center gap-2 data-[state=active]:bg-orange data-[state=active]:text-white">
              <Shield className="w-4 h-4" />
              Insurance Referrals ({referralStats.total})
            </TabsTrigger>
          </TabsList>

          {/* Storm Damage Leads Tab */}
          <TabsContent value="leads">
            {/* Lead Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              {[
                { label: "Total Leads", value: stats.total, color: "bg-navy" },
                { label: "New", value: stats.new, color: "bg-blue-500" },
                { label: "Contacted", value: stats.contacted, color: "bg-yellow-500" },
                { label: "In Progress", value: stats.in_progress, color: "bg-purple-500" },
                { label: "Completed", value: stats.completed, color: "bg-green-500" },
              ].map((stat, idx) => (
                <div
                  key={`lead-stat-${idx}`}
                  className="bg-white border border-gray-200 rounded-xl p-4"
                >
                  <div className={`w-2 h-2 rounded-full ${stat.color} mb-2`} />
                  <div className="text-2xl font-heading font-bold text-navy">
                    {stat.value}
                  </div>
                  <div className="text-gray-500 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Lead List */}
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading leads...</div>
            ) : leads.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="font-heading text-lg font-semibold text-navy mb-2">
                  No Leads Yet
                </h3>
                <p className="text-gray-500">
                  Leads will appear here when submitted through the website.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {leads.map((lead) => {
                  const StatusIcon = statusConfig[lead.status]?.icon || AlertCircle;
                  return (
                    <div
                      key={lead.id}
                      data-testid={`lead-card-${lead.id}`}
                      className="bg-white border border-gray-200 rounded-xl p-6 hover:border-orange/30 transition-colors"
                    >
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <h3 className="font-heading text-lg font-semibold text-navy">
                              {lead.name}
                            </h3>
                            <Badge
                              className={`${
                                statusConfig[lead.status]?.color
                              } border-0`}
                            >
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig[lead.status]?.label}
                            </Badge>
                          </div>

                          <div className="grid sm:grid-cols-2 gap-3 text-sm">
                            <div className="flex items-center gap-2 text-gray-600">
                              <Mail className="w-4 h-4 text-gray-400" />
                              <a
                                href={`mailto:${lead.email}`}
                                className="hover:text-orange"
                              >
                                {lead.email}
                              </a>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                              <Phone className="w-4 h-4 text-gray-400" />
                              <a
                                href={`tel:${lead.phone}`}
                                className="hover:text-orange"
                              >
                                {lead.phone}
                              </a>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              <span>
                                {lead.address}, {lead.city}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <span>{formatDate(lead.created_at)}</span>
                            </div>
                          </div>

                          <div className="mt-3 flex items-start gap-2">
                            <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                            <div>
                              <span className="text-sm font-medium text-navy">
                                Situation:
                              </span>
                              <span className="text-sm text-gray-600 ml-2">
                                {situationLabels[lead.situation] || lead.situation}
                              </span>
                            </div>
                          </div>

                          {lead.message && (
                            <div className="mt-2 bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                              {lead.message}
                            </div>
                          )}
                        </div>

                        <div className="flex-shrink-0">
                          <Select
                            value={lead.status}
                            onValueChange={(value) =>
                              updateLeadStatus(lead.id, value)
                            }
                          >
                            <SelectTrigger className="w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(statusConfig).map(([key, config]) => (
                                <SelectItem key={key} value={key}>
                                  {config.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Insurance Referrals Tab */}
          <TabsContent value="referrals">
            {/* Referral Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: "Total Referrals", value: referralStats.total, color: "bg-navy" },
                { label: "New", value: referralStats.new, color: "bg-blue-500" },
                { label: "Contacted", value: referralStats.contacted, color: "bg-yellow-500" },
                { label: "Completed", value: referralStats.completed, color: "bg-green-500" },
              ].map((stat, idx) => (
                <div
                  key={`ref-stat-${idx}`}
                  className="bg-white border border-gray-200 rounded-xl p-4"
                >
                  <div className={`w-2 h-2 rounded-full ${stat.color} mb-2`} />
                  <div className="text-2xl font-heading font-bold text-navy">
                    {stat.value}
                  </div>
                  <div className="text-gray-500 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Referral List */}
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading referrals...</div>
            ) : referrals.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="font-heading text-lg font-semibold text-navy mb-2">
                  No Insurance Referrals Yet
                </h3>
                <p className="text-gray-500">
                  Insurance agent referral requests will appear here.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {referrals.map((referral) => {
                  const StatusIcon = statusConfig[referral.status]?.icon || AlertCircle;
                  return (
                    <div
                      key={referral.id}
                      data-testid={`referral-card-${referral.id}`}
                      className="bg-white border border-gray-200 rounded-xl p-6 hover:border-orange/30 transition-colors"
                    >
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <h3 className="font-heading text-lg font-semibold text-navy">
                              {referral.name}
                            </h3>
                            <Badge className="bg-purple-100 text-purple-700 border-0">
                              <Shield className="w-3 h-3 mr-1" />
                              Insurance Referral
                            </Badge>
                            <Badge
                              className={`${
                                statusConfig[referral.status]?.color
                              } border-0`}
                            >
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig[referral.status]?.label}
                            </Badge>
                          </div>

                          <div className="grid sm:grid-cols-2 gap-3 text-sm">
                            <div className="flex items-center gap-2 text-gray-600">
                              <Mail className="w-4 h-4 text-gray-400" />
                              <a
                                href={`mailto:${referral.email}`}
                                className="hover:text-orange"
                              >
                                {referral.email}
                              </a>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                              <Phone className="w-4 h-4 text-gray-400" />
                              <a
                                href={`tel:${referral.phone}`}
                                className="hover:text-orange"
                              >
                                {referral.phone}
                              </a>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              <span>{referral.city}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <span>{formatDate(referral.created_at)}</span>
                            </div>
                          </div>

                          <div className="mt-3 flex items-start gap-2">
                            <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                            <div>
                              <span className="text-sm font-medium text-navy">
                                Reason:
                              </span>
                              <span className="text-sm text-gray-600 ml-2">
                                {insuranceReasonLabels[referral.reason] || referral.reason}
                              </span>
                            </div>
                          </div>

                          {referral.message && (
                            <div className="mt-2 bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                              {referral.message}
                            </div>
                          )}
                        </div>

                        <div className="flex-shrink-0">
                          <Select
                            value={referral.status}
                            onValueChange={(value) =>
                              updateReferralStatus(referral.id, value)
                            }
                          >
                            <SelectTrigger className="w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(statusConfig).map(([key, config]) => (
                                <SelectItem key={key} value={key}>
                                  {config.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
